import { signIn } from "aws-amplify/auth";
import handleEmailValidation from "../utils/emailValidator";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { toast } from "@/lib/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useDispatch } from "react-redux";
import { setEmail, setPermissions } from "@/state/store/slice/userSlice";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { PasswordInput } from "@/components/ui/password-input";
import { useForm } from "react-hook-form";
import { useEffect } from "react";
import { Label } from "@/components/ui/label";
import { getUserPermissions } from "./GetUserPermissions";

// Helper to get a safe error message from unknown
const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "";
};

const imgURL = `assets/images/${import.meta.env.VITE_REACT_APP_STAGE}/revanista-logo.png`;

//Define zod form
const formSchema = z.object({
  email: z
    .string()
    .min(1, "Please provide your email")
    .refine((val) => handleEmailValidation(val), {
      message: "Invalid email address",
    }),
  password: z.string().min(1, "Password is required"),
});

const SignIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
    },
    mode: "onChange",
  });

  const dispatch = useDispatch();

  //Check if user is redirected because of being unauthorized
  useEffect(() => {
    const message = sessionStorage.getItem("toastMessage");
    if (message) {
      toast.error(message);
      sessionStorage.removeItem("toastMessage");
    }
  }, []);

  const isValid = form.formState.isValid;

  // Prefill email from query parameter (?prefill=foo@bar.com)
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const prefill = params.get("prefill");
      if (prefill && typeof prefill === "string") {
        form.setValue("email", prefill, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    } catch (error: unknown) {
      console.log("Error pre-filling", getErrorMessage(error));
    }
  }, [location.search, form]);

  //Use aws amplify signin api to signin user
  const onSignIn = async (values: z.infer<typeof formSchema>) => {
    try {
      const result = await signIn({
        username: values.email.toLowerCase(),
        password: values.password,
        options: {
          authFlowType: "USER_SRP_AUTH",
        },
      });

      // Check if user needs to set a new password first
      const step = result?.nextStep?.signInStep;
      if (step === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        sessionStorage.setItem("firstSignIn", "true");
        toast.info("You need to set a new password to complete sign in.");
        navigate("/update-password", { replace: true });
        return;
      }

      // Only get user permissions after confirming it's not a first sign-in
      const { email, permissions } = await getUserPermissions();
      dispatch(setEmail(email));
      dispatch(setPermissions(permissions));
      localStorage.setItem("email", email);

      if (result?.isSignedIn) {
        const params = new URLSearchParams(location.search);
        const redirectTo = params.get("redirect");
        navigate(redirectTo ? decodeURIComponent(redirectTo) : "/", {
          replace: true,
        });
        return;
      }

      // Fallback for other next steps (e.g., MFA), not handled here
      if (result?.nextStep) {
        toast.info("Additional verification is required to complete sign in.");
      }
    } catch (error: unknown) {
      const message = getErrorMessage(error);
      console.log(message);
      if (message === "User does not exist.") {
        toast.error("User does not exist. Please contact your administrator.");
      } else if (message === "Incorrect username or password.") {
        toast.error("Incorrect email or password.");
      } else if (message === "There is already a signed in user.") {
        navigate("/");
      } else {
        toast.error(message || "Sign in failed");
      }
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center px-4">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSignIn)}
          className="w-full max-w-md space-y-8 py-10"
        >
          <div className="w-full rounded-lg bg-white p-8 shadow-2xl sm:p-10">
            <fieldset className="p-2 sm:p-5">
              <div className="mb-4">
                <img
                  className="mx-auto h-auto w-3/4"
                  src={imgURL}
                  alt="Revanista Logo"
                />
              </div>
              <div className="mt-8">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="text-lg">
                      <FormLabel className="text-lg text-black">
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          className="h-12 text-lg text-black focus:outline-red-500"
                          placeholder="Email"
                          type="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-5">
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem className="text-lg">
                      <FormLabel className="text-lg text-black">
                        Password
                      </FormLabel>
                      <FormControl>
                        <PasswordInput
                          className="h-12 text-lg text-black"
                          placeholder="Password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-7">
                <Button
                  type="submit"
                  className="mt-4 w-full rounded-lg bg-red-700 text-white shadow-xl hover:cursor-pointer"
                  disabled={!isValid}
                >
                  Login
                </Button>
              </div>
              <Label className="mt-3 text-left text-xl">
                <NavLink
                  to="/forgot-password"
                  className="text-red-500 hover:underline"
                >
                  Forgot password?
                </NavLink>
              </Label>
            </fieldset>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default SignIn;
